import React from 'react';
import { Clock, Brain, ChevronRight } from 'lucide-react';
import type { RecipientFlow } from '../types';

interface RecipientChoiceProps {
  recipientName: string;
  setRecipientName: (name: string) => void;
  onFlowChoice: (flow: RecipientFlow) => void;
}

export const RecipientChoice: React.FC<RecipientChoiceProps> = ({
  recipientName,
  setRecipientName,
  onFlowChoice
}) => {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">
          Now let's profile your gift recipient
        </h1>
        <p className="text-gray-600 text-lg">
          Choose how much detail you'd like to provide
        </p>
      </div>

      {/* Recipient Name Input */}
      <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center">
          What's their name?
        </h2>
        <div className="max-w-md mx-auto">
          <input
            type="text"
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
            placeholder="Enter their name..."
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:outline-none text-center text-lg transition-colors"
          />
        </div>
      </div>

      {/* Flow Choice Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Quick Add Option */}
        <div
          onClick={() => recipientName.trim() && onFlowChoice('quick')}
          className={`bg-white rounded-3xl shadow-xl p-8 cursor-pointer transition-all hover:shadow-2xl hover:scale-105 ${
            !recipientName.trim() ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
              <Clock className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Quick Add</h3>
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="text-3xl font-bold text-green-600">≤60s</span>
              <span className="text-gray-500">• 4 questions</span>
            </div>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Get the basics and start getting recommendations quickly. 
              You can always come back to add more details later.
            </p>
            
            <div className="space-y-3 text-sm text-gray-500 mb-6">
              <div className="flex items-center justify-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Relationship & duration</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Quick personality tags</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Budget & preferences</span>
              </div>
            </div>

            <button
              disabled={!recipientName.trim()}
              className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white rounded-xl font-medium transition-colors"
            >
              Quick Start <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Full Profile Option */}
        <div
          onClick={() => recipientName.trim() && onFlowChoice('full')}
          className={`bg-white rounded-3xl shadow-xl p-8 cursor-pointer transition-all hover:shadow-2xl hover:scale-105 ${
            !recipientName.trim() ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-6">
              <Brain className="w-8 h-8 text-indigo-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Full Profile</h3>
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="text-3xl font-bold text-indigo-600">~5min</span>
              <span className="text-gray-500">• 11 questions</span>
            </div>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Deep psychological profiling for eerily accurate recommendations. 
              Based on validated personality research.
            </p>
            
            <div className="space-y-3 text-sm text-gray-500 mb-6">
              <div className="flex items-center justify-center gap-2">
                <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                <span>Personality & values assessment</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                <span>Gift reaction patterns</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                <span>Detailed interests & context</span>
              </div>
            </div>

            <button
              disabled={!recipientName.trim()}
              className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white rounded-xl font-medium transition-colors"
            >
              Full Analysis <Brain className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="text-center mt-8">
        <p className="text-gray-500 text-sm">
          💡 Pro tip: Full profiles lead to much more accurate and personalized recommendations
        </p>
      </div>
    </div>
  );
}; 